<template>
  <!-- Clipped sideways, where the tabs slide, but never downwards: the ADAC
       flyout hangs below the panel, absolutely, and a root that clips on both
       axes — `overflow-x: hidden` makes the other one scroll — would cut it at
       the panel's height however tall the window. -->
  <div
    ref="notificationRef"
    class="flex flex-1 overflow-x-clip"
    :class="notificationStore.edge === 'left' ? 'justify-start' : 'justify-end'"
  >
    <component
      :is="component"
      v-if="component"
      :item="item"
      :detecting="detecting"
      :error="detectionError"
      :has-candidate-choice="!!candidateChoice"
      @resize="resizeNotification"
      @pick-candidate="pickCandidate"
      @back-to-candidates="backToCandidates"
      @read-vitale-card="readVitaleCard"
    />
  </div>
</template>

<script lang="ts" setup>
import NotificationBroadcast from "@/notification/components/notification-broadcast/NotificationBroadcast.vue";
import useNotification from "@/notification/composables/useNotification";
import useEnvironment from "@/notification/composables/useEnvironment";
import useCustomerDetectionIpcEvents, {
    DetectionDriverSlug
} from "@/notification/composables/useCustomerDetectionIpcEvents";
import {markRaw, nextTick, onMounted, onUnmounted, ref, watch} from "vue";
import useBroadcastNotification from "@/notification/composables/useBroadcastNotification";
import useRemoteAssistance from "@/notification/composables/useRemoteAssistance";
import useCpsLocator from "@/amelipro/composables/useCpsLocator";
import NotificationDetection from "@/notification/components/notification-detection/NotificationDetection.vue";
import {UserSettings} from "@/settings/interfaces";
import {
    BroadcastNotificationProps,
    DetectionCandidate,
    DetectionErrorProps,
    DetectionNotificationProps,
    DetectionPatient
} from "@/notification/interfaces";
import {storeToRefs} from "pinia";
import {useNotificationStore} from "@/stores/notification.store";
import NotificationPatientDetectionService from "@/notification/service/NotificationPatientDetectionService";
import StorageService from "@/core/services/StorageService";
import {VitaleCardReadResult} from "@/core/interfaces/detection/detection";
import {LivePatientContext, startLivePatientContext} from "@/live/services/livePatientContext";
import {startLiveServiceUpdates} from "@/live/services/liveServiceUpdate";
import {LivePatientEvent} from "@/live/interfaces";
import {recordLiveOperator} from "@/live/services/liveOperator";

const notificationStore = useNotificationStore();
const {cachedPatients} = storeToRefs(notificationStore);
const {loadPatientCache} = notificationStore;

const notificationRef = ref<HTMLElement | null>(null);
const {component, item, detecting, showNotification, resizeNotification} = useNotification(notificationRef);
const environment = useEnvironment();
const hostName = ref<string | null>(null);
const customerDetection = useCustomerDetectionIpcEvents();
let intervalId: ReturnType<typeof setInterval>;
/** Retries the environment until the organization is known, so the pharmacy channel gets joined. */
let environmentRetry: ReturnType<typeof setInterval> | null = null;
/** The LIVE patient watch, on shells that can listen to the LGO traffic. */
let livePatientContext: LivePatientContext | null = null;
let stopLiveServiceUpdates: (() => void) | null = null;
const currentDetectedPatientData = ref<DetectionPatient | null>(null);
/**
 * The last raw payload received from the OCR loop, notified or not. While the
 * LGO screen does not change, the loop keeps sending the same payload every
 * tick: remembering it tells an unchanged screen apart from a new patient
 * file, so a Vitale card read is not overwritten by the file left open behind.
 */
const lastOcrPatientData = ref<DetectionPatient | null>(null);
const detectionError = ref<DetectionErrorProps | null>(null);
const lastDetection = ref<{ settings: UserSettings, driver: DetectionDriverSlug } | null>(null);

/**
 * The pending ambiguity: kept after the pharmacist picks a candidate, so the
 * notification can offer to come back and pick another one.
 */
const candidateChoice = ref<DetectionErrorProps | null>(null);

/**
 * Run the detection and show the result: the patient's programs, or the
 * detection error (possibly the candidate picker on an ambiguous detection).
 */
const runDetection = async (detectionService: NotificationPatientDetectionService, patient: DetectionPatient) => {
    component.value = markRaw(NotificationDetection);
    detecting.value = patient;

    try {
        const data = await detectionService.fetchPrograms();

        // Turn the loader off before showing: showNotification sizes the window on the
        // rendered content, which must be the full panel and not the loader body.
        detecting.value = null;

        if (!data) {
            return;
        }

        detectionError.value = null;

        // Also compare the detected consumer: the same customer can be re-detected
        // through one of its beneficiaries (e.g. the parent's file, then the child's).
        const current = item.value as DetectionNotificationProps | null;
        const anotherPatient = current?.customer?.id !== data.customer?.id
            || current?.detectedConsumer?.key !== data.detectedConsumer?.key;

        // The fresh response always: a later detection of the same patient may
        // bring what the first lacked — a NIR read off the card, a targeting.
        // The window is only shown again for another patient.
        item.value = data;

        if (anotherPatient) {
            await showNotification();
        }
    } catch (error) {
        detecting.value = null;

        const builtError = detectionService.buildError(error);

        // The candidate picker asks the pharmacist to act: it must be shown. The
        // ambiguity is also kept so the notification can come back to it after a pick.
        if (builtError.candidates?.length) {
            detectionError.value = builtError;
            item.value = null;
            candidateChoice.value = builtError;
            await showNotification();

            return;
        }

        // A detection that could not be verified (no checksum-valid NIR, no
        // patient cache match) only reached the API through a filter bypass.
        // When it fails there, treat it as OCR noise (e.g. an emptied LGO
        // form): keep the displayed patient instead of replacing them with
        // an error.
        if (item.value && !patient.nir && !patient.key) {
            return;
        }

        detectionError.value = builtError;
        item.value = null;
    }
};

/**
 * Show the candidate picker again, after the pharmacist picked one and wants
 * to look at another candidate.
 */
const backToCandidates = async () => {
    if (!candidateChoice.value) {
        return;
    }

    item.value = null;
    detecting.value = null;
    detectionError.value = candidateChoice.value;

    await showNotification();
};

/**
 * Retry the detection with the consumer the pharmacist picked in the
 * ambiguity notification.
 */
const pickCandidate = async (candidate: DetectionCandidate) => {
    if (!lastDetection.value) {
        return;
    }

    // No birth date: the consumer key is enough, and the API expects its own
    // date format on that field.
    const patient: DetectionPatient = {
        key: candidate.consumer_key,
        firstName: candidate.first_name ?? undefined,
        lastName: candidate.last_name ?? undefined
    };

    const detectionService = new NotificationPatientDetectionService(
        patient,
        hostName.value,
        lastDetection.value.settings,
        lastDetection.value.driver
    );

    await runDetection(detectionService, patient);
};

/**
 * Callback for handling broadcast notifications.
 *
 * @param data
 * @param height
 */
const handleBroadcastNotification = (data: BroadcastNotificationProps, height: number) => {
    component.value = markRaw(NotificationBroadcast);
    item.value = data;

    showNotification(height);
};

const broadcast = useBroadcastNotification(
    handleBroadcastNotification
);
const remoteAssistance = useRemoteAssistance();
const cpsLocator = useCpsLocator();

/**
 * Opening the drawer restores the window bounds saved at collapse time, which
 * may not match the current content anymore (e.g. the empty state grew a
 * button): fit the height on the rendered drawer.
 */
watch(() => notificationStore.isOpen, async (isOpen) => {
    if (isOpen) {
        await resizeNotification();
    }
});

/**
 * Run the detection flow for an extracted patient, coming from the polling
 * OCR detection or from an on-demand Vitale card read.
 *
 * @returns Whether the patient passed the notifiable filters.
 */
const handleExtractedPatient = async (patient: DetectionPatient, settings: UserSettings, driver: DetectionDriverSlug): Promise<boolean> => {
    // Clone the raw payload before the service mutates the patient (cleaned names, cache key),
    // so the deduplication compares payloads of the same shape from one event to the next.
    const rawPatient = structuredClone(patient);

    const detectionService = new NotificationPatientDetectionService(patient, hostName.value, settings, driver);

    // OCR payload identical to the previous tick: the pharmacist is still on the
    // same patient file, nothing new to detect. Only a changed screen may take
    // over the current patient (typically the one just read on the Vitale card).
    // Payloads carrying a consumer key are emulations, never OCR text: they are
    // explicit actions and are not remembered here. Nor is anything received
    // before the hostname is known, since the service rejects it regardless.
    if (driver === 'cardless' && !rawPatient.key && hostName.value) {
        const screenUnchanged = detectionService.isSamePatientData(rawPatient, lastOcrPatientData.value);

        lastOcrPatientData.value = rawPatient;

        if (screenUnchanged) {
            return false;
        }
    }

    const notifiablePatient = await detectionService.notifiablePatient(currentDetectedPatientData.value, cachedPatients.value);

    if (!notifiablePatient) {
        return false;
    }

    currentDetectedPatientData.value = rawPatient;

    lastDetection.value = {settings, driver};
    candidateChoice.value = null; // a new detection voids the previous ambiguity

    await runDetection(detectionService, notifiablePatient);

    return true;
};

/**
 * Map a `detection:read-card` error to a user-facing message.
 */
const readErrorMessage = (error?: string): string => {
    switch (error) {
        case 'No card inserted':
            return 'Aucune carte Vitale détectée dans le lecteur.';
        case 'No card reader detected':
            return 'Aucun lecteur de carte Vitale détecté.';
        case 'Card read timeout':
            return 'La lecture de la carte a expiré.';
        case 'Card read already in progress':
            return 'Une lecture est déjà en cours.';
        case 'Sesam Vitale unavailable':
            return 'La lecture de carte Vitale n\'est pas disponible sur ce poste.';
        default:
            // Never surface a raw driver message to the pharmacist.
            return 'La lecture de la carte Vitale a échoué.';
    }
};

/**
 * Read the Vitale card on demand and run the detection flow on the result.
 */
const readVitaleCard = async () => {
    if (detecting.value) {
        return;
    }

    const settings = await StorageService().read('userSettings') as UserSettings;

    // Empty placeholder (no nir, no key): the header shows "Détection en cours …"
    // while the reader powers the card, and the actions row is hidden.
    component.value = markRaw(NotificationDetection);
    detectionError.value = null;
    detecting.value = {} as DetectionPatient;

    // Let the spinner paint before the read starts.
    await nextTick();

    let result: VitaleCardReadResult | null = null;

    try {
        // Named preload method when the shell exposes it (the capability the
        // read UI is gated on); raw invoke as a safety net for intermediate
        // shells having the handler without the method.
        result = await (window.ipcRenderer.readVitaleCard
            ? window.ipcRenderer.readVitaleCard()
            : window.ipcRenderer.invoke('detection:read-card')) as VitaleCardReadResult | null;
    } catch (error) {
        // Blame the app version only when the handler truly does not exist:
        // any other rejection is a real read failure on a capable shell.
        const missingHandler = error instanceof Error && error.message.includes('No handler registered');

        detecting.value = null;
        item.value = null;
        detectionError.value = {
            title: 'Carte Vitale non lue',
            message: missingHandler
                ? "Cette version d'Apodis Connect ne prend pas encore en charge la lecture à la demande."
                : readErrorMessage(undefined),
            settings,
        };

        if (notificationStore.isOpen) {
            await showNotification();
        }

        return;
    }

    if (!result || 'error' in result) {
        // Same behavior as a failed OCR detection: the drawer is not unfolded.
        detecting.value = null;
        item.value = null;
        detectionError.value = {
            title: 'Carte Vitale non lue',
            message: readErrorMessage(result?.error),
            settings,
        };

        // When the drawer is already open, fit the window to the error banner
        // (it may still be at the empty-panel height, clipping the banner).
        if (notificationStore.isOpen) {
            await showNotification();
        }

        return;
    }

    // Explicit pharmacist action: forget the previous payload so re-reading
    // the same card triggers a new detection.
    currentDetectedPatientData.value = null;

    if (!await handleExtractedPatient(result.patient as DetectionPatient, settings, 'sesam-vitale')) {
        detecting.value = null;
    }
};

/**
 * LIVE: a patient taken at this counter, read by the shell from the
 * workstation's own LGO traffic, with no action from the pharmacist. Always
 * through the HDS `live` driver, which records the detection as a network
 * read on the host: with the LGO's own patient id (LGPI, PharmaVitale), by
 * software key then by the NIR of the card just read; with a NIR alone
 * (Winpharma), as a Vitale card read would.
 */
const handleLiveSelection = async (event: LivePatientEvent) => {
    if (!event.nir && !event.patientRef) {
        return;
    }

    const settings = await StorageService().read('userSettings') as UserSettings;

    // A new selection at the counter: the same patient taken again is detected again.
    currentDetectedPatientData.value = null;

    // LGPI gives its own patient id, and the NIR of a Vitale card just read:
    // the HDS `live` driver finds the patient by its software key (the very
    // person, a beneficiary included), failing that by the NIR, making a
    // guest for a patient the sales have not brought yet.
    if (event.patientRef) {
        await handleExtractedPatient({softwareKey: event.patientRef, ...event.nir ? {nir: event.nir} : {}}, settings, 'live');
        return;
    }

    await handleExtractedPatient({nir: event.nir}, settings, 'live');
};

onMounted(async () => {
    window.ipcRenderer.on('extract-patient', async (_: Event, patient: DetectionPatient, settings: UserSettings, driver: DetectionDriverSlug) => {
        await handleExtractedPatient(patient, settings, driver);
    });

    window.ipcRenderer.on('reset-patient', (_: Event, patient: DetectionPatient, driver: DetectionDriverSlug) => {
        if (!hostName.value || !patient) {
            return;
        }

        customerDetection.resetPatient(patient, hostName.value, driver);
    });

    window.ipcRenderer.on('preview-detection', async (_: Event, settings: UserSettings) => {
        item.value = customerDetection.previewItem(settings);
        component.value = markRaw(NotificationDetection);

        await showNotification();
    });

    // Sent by the main process instead of hiding the window (broadcast close,
    // navigation to the main window…): fold back to the handle.
    window.ipcRenderer.on('collapse-notification', () => {
        if (component.value !== NotificationDetection) {
            item.value = null;
            component.value = markRaw(NotificationDetection);
        }

        notificationStore.closeDrawer();
    });

    // Show the collapsed handle first: it depends on nothing below, and it
    // must survive a failure of any loading step (page reloads used to leave
    // the window zero-sized and invisible until the next detection).
    component.value = markRaw(NotificationDetection);

    await showNotification();

    if (!item.value) {
        notificationStore.closeDrawer();
    }

    await environment.loadEnvironment();

    hostName.value = await window.ipcRenderer.invoke('getHostname');

    // A missing cache is a documented degraded mode: never abort the boot on it.
    await loadPatientCache().catch(() => null);
    intervalId = setInterval(loadPatientCache, 60 * 60 * 1000);

    // Nothing on shells without the capability, nor for an LGO without LIVE
    // support or a workstation without capture driver: the OCR and the
    // on-demand card read go on as before.
    livePatientContext = await startLivePatientContext(handleLiveSelection, (operator) => {
        recordLiveOperator(operator, hostName.value);
    }).catch(() => null);
    // Keeps the Apodis Live service at the version the API offers.
    stopLiveServiceUpdates = startLiveServiceUpdates();

    broadcast.subscribe(environment.environment.value?.userId);
    remoteAssistance.subscribe(environment.environment.value?.organizationId, hostName.value);
    // This window owns the card reader, so it is the one that answers the
    // pharmacy's CPS location requests.
    cpsLocator.subscribe(environment.environment.value?.organizationId, hostName.value, {answers: true});

    // The environment can fail to load at boot — the main window has not
    // signed in yet, the API was out of reach for a second — and every
    // subscription above silently does nothing without an organization: the
    // pharmacy channel is never joined and this post never announces its
    // reader, until the next restart. Nothing else retries; this does, until
    // the organization is known.
    if (!environment.environment.value?.organizationId) {
        environmentRetry = setInterval(async () => {
            await environment.loadEnvironment();

            const organizationId = environment.environment.value?.organizationId;

            if (!organizationId) {
                return;
            }

            if (environmentRetry) {
                clearInterval(environmentRetry);
                environmentRetry = null;
            }

            broadcast.subscribe(environment.environment.value?.userId);
            remoteAssistance.subscribe(organizationId, hostName.value);
            cpsLocator.subscribe(organizationId, hostName.value, {answers: true});
        }, 20_000);
    }
});

onUnmounted(() => {
    clearInterval(intervalId);

    if (environmentRetry) {
        clearInterval(environmentRetry);
    }
    remoteAssistance.unsubscribe();
    cpsLocator.unsubscribe();
    livePatientContext?.stop();
    stopLiveServiceUpdates?.();
    window.ipcRenderer.removeAllListeners('extract-patient');
    window.ipcRenderer.removeAllListeners('reset-patient');
    window.ipcRenderer.removeAllListeners('preview-detection');
    window.ipcRenderer.removeAllListeners('collapse-notification');
});
</script>
